import Keycloak from "keycloak-js";
import { useStore } from "@/stores/store.js";
import * as Sentry from "@sentry/react";
import * as gtm from "@/utils/gtm";
import { getAttribution } from "@/utils/attribution";
import { diagnosticEventLog } from "@/shared/services/diagnostic";
import toast from "react-hot-toast";

// Optionally set default values for development or fallback
const keycloakConfig = {
  realm: "Newsquawk",
  url: import.meta.env.VITE_KEYCLOAK_URL || "https://auth.uat.newsquawk.com/",
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || "react-frontend-client",
};

// Initialize Keycloak instance
const keycloakInstance = new Keycloak(keycloakConfig);


// --- Resilient logout helper ---
// Prevents transient network failures from destroying a valid SSO session.
// Flow: capture to Sentry → retry once → silent re-auth → toast → logout
let isHandlingRefreshFailure = false;

function getTokenDiagnostics() {
  const tokenExp = keycloakInstance.tokenParsed?.exp;
  const now = Math.floor(Date.now() / 1000);
  return {
    tokenExpiry: tokenExp,
    currentTime: now,
    secondsUntilExpiry: tokenExp ? tokenExp - now : null,
    visibilityState: document.visibilityState,
    authenticated: keycloakInstance.authenticated,
  };
}

export async function handleTokenRefreshFailure(
  source: string,
  error?: unknown
): Promise<void> {
  // Prevent concurrent handling from multiple triggers (e.g. onAuthRefreshError + catch)
  if (isHandlingRefreshFailure) return;
  isHandlingRefreshFailure = true;

  const diagnostics = getTokenDiagnostics();

  diagnosticEventLog.record("auth:app", "error", "token-refresh-failed", {
    source,
    ...diagnostics,
    error: String(error ?? ""),
  });

  // Don't capture to Sentry here — most failures recover via retry or silent re-auth.
  // Only capture at step 3 when the user is actually being forced out.
  Sentry.addBreadcrumb({
    category: "auth",
    message: `Token refresh failed (${source}), attempting recovery`,
    level: "warning",
    data: diagnostics,
  });

  console.warn(`Token refresh failed (${source}). Retrying...`);

  // Step 1: Retry once after a short delay (handles transient network blips)
  try {
    await new Promise((r) => setTimeout(r, 3000));
    const refreshed = await keycloakInstance.updateToken(5);
    if (refreshed || keycloakInstance.authenticated) {
      useStore.setState({ authenticated: true });
      console.info(`Token refresh retry succeeded (${source}).`);
      diagnosticEventLog.record("auth:app", "info", "token-refresh-retry-succeeded", { source });
      isHandlingRefreshFailure = false;
      return;
    }
  } catch {
    // Retry failed, continue to silent re-auth
  }

  // Step 2: Session is unrecoverable — notify user and logout
  console.error(`Session expired (${source}). Logging out.`);
  diagnosticEventLog.record("auth:app", "error", "session-expired-logout", { source });

  Sentry.withScope((scope) => {
    scope.setTag("auth_failure_source", source);
    scope.setTag("visibility_state", diagnostics.visibilityState);
    scope.setLevel("error");
    scope.setContext("token_state", diagnostics);
    Sentry.captureMessage(`Forced logout — session unrecoverable (${source})`);
  });
  toast.error("Your session has expired. Please sign in again.", {
    duration: 5000,
  });

  // Brief delay so the toast is visible before the redirect
  await new Promise((r) => setTimeout(r, 1500));
  isHandlingRefreshFailure = false;
  try { localStorage.removeItem("nq:last-auth-user"); } catch { /* best-effort */ }
  keycloakInstance.logout();
}

// --- Keycloak event listeners ---

keycloakInstance.onTokenExpired = () => {
  diagnosticEventLog.record("auth:app", "warn", "token-expired");
  Sentry.addBreadcrumb({ category: "auth", message: "Token expired", level: "warning" });
  console.info("Token expired. Attempting token refresh...");

  keycloakInstance
    .updateToken(60)
    .then((refreshed) => {
      if (!refreshed) {
        console.warn("Token could not be refreshed.");
        handleTokenRefreshFailure("onTokenExpired:not-refreshed");
      }
    })
    .catch((error) => {
      handleTokenRefreshFailure("onTokenExpired:error", error);
    });
};

keycloakInstance.onAuthRefreshSuccess = () => {
  useStore.setState({ authenticated: true });
  useStore.getState().setAccessToken(keycloakInstance.token);
  useStore.getState().setTokenParsed(keycloakInstance.tokenParsed);
  diagnosticEventLog.record("auth:app", "info", "token-refreshed");
  Sentry.addBreadcrumb({ category: "auth", message: "Token refreshed", level: "info" });
  console.info("Token refreshed successfully.");
};

keycloakInstance.onAuthRefreshError = () => {
  // Only update state here — do NOT call logout().
  // The caller's .catch() handler drives the resilient logout flow,
  // preventing double-fire with this callback.
  diagnosticEventLog.record("auth:app", "error", "auth-refresh-error-callback");
  Sentry.addBreadcrumb({ category: "auth", message: "Auth refresh error callback", level: "error" });
  useStore.setState({ authenticated: false, accessToken: null });
  console.error("Auth refresh error callback fired.");
};

keycloakInstance.onAuthLogout = async () => {
  diagnosticEventLog.record("auth:app", "warn", "auth-logout-callback-fired");
  Sentry.addBreadcrumb({ category: "auth", message: "onAuthLogout fired — verifying session", level: "warning" });
  console.warn("onAuthLogout fired — verifying session before logging out...");

  // The SSO iframe detected the session is gone, but this can be a false positive
  // (corporate proxy, Chrome background throttling, network blip).
  // Verify by attempting a token refresh before committing to logout.
  try {
    const refreshed = await keycloakInstance.updateToken(5);
    if (refreshed || keycloakInstance.authenticated) {
      diagnosticEventLog.record("auth:app", "info", "auth-logout-false-positive");
      Sentry.addBreadcrumb({ category: "auth", message: "onAuthLogout was false positive — session still valid", level: "info" });
      console.info("onAuthLogout was a false positive — session is still valid.");
      return;
    }
  } catch {
    // Token refresh failed — session is genuinely dead (e.g. killed by duplicate login)
  }

  // Session is confirmed dead — capture to Sentry before clearing user context
  Sentry.withScope((scope) => {
    scope.setTag("auth_failure_source", "onAuthLogout");
    scope.setTag("visibility_state", document.visibilityState);
    scope.setLevel("error");
    Sentry.captureMessage("Session ended — confirmed via onAuthLogout (likely duplicate login)");
  });

  useStore.setState({
    authenticated: false,
    accessToken: null,
  });

  Sentry.setUser(null);
  gtm.trackLogout();
  gtm.clearUser();

  toast.error("Your session has ended. Please sign in again.", {
    duration: 5000,
  });

  console.info("Session confirmed dead — redirecting to login.");
  await new Promise((r) => setTimeout(r, 1500));
  try { localStorage.removeItem("nq:last-auth-user"); } catch { /* best-effort */ }
  keycloakInstance.logout();
};

// Set keycloak instance in zustand store
keycloakInstance
  .init({
    onLoad: "check-sso",
    redirectUri:
      import.meta.env.VITE_KEYCLOAK_REDIRECT_URI || "https://app.newsquawk.com",
    silentCheckSsoRedirectUri: window.location.origin + "/silent-check-sso.html",
    silentCheckSsoFallback: false,
    checkLoginIframe: false,
  })
  .then(() => {
    useStore.setState({
      keycloakInstance: keycloakInstance as Keycloak,
      authenticated: keycloakInstance.authenticated,
      accessToken: keycloakInstance.token,
    });

    useStore.getState().setTokenParsed(keycloakInstance.tokenParsed);

    diagnosticEventLog.record("auth:app", "info", "keycloak-init", {
      authenticated: !!keycloakInstance.authenticated,
    });

    // Set user to sentry + persist for init-failure attribution
    if (keycloakInstance.authenticated) {
      const userInfo = {
        id: keycloakInstance.tokenParsed?.sub,
        username: keycloakInstance.tokenParsed?.preferred_username,
        email: keycloakInstance.tokenParsed?.email,
      };
      Sentry.setUser(userInfo);
      try {
        localStorage.setItem("nq:last-auth-user", JSON.stringify(userInfo));
      } catch {
        // localStorage may be unavailable in some corporate environments
      }
      const attr = getAttribution();
      gtm.trackLogin({
        method: "keycloak",
        user_id: keycloakInstance.tokenParsed?.sub,
        ...(attr && {
          utm_source: attr.utm_source,
          utm_medium: attr.utm_medium,
          utm_campaign: attr.utm_campaign,
          utm_content: attr.utm_content,
          utm_term: attr.utm_term,
          referrer_domain: attr.referrer_domain,
          landing_page: attr.landing_page,
        }),
      });
    }
  })
  .catch((error) => {
    // Recover last-known user so Sentry events are attributed
    try {
      const lastUser = localStorage.getItem("nq:last-auth-user");
      if (lastUser) {
        Sentry.setUser(JSON.parse(lastUser));
      }
    } catch {
      // best-effort
    }

    const errorMessage = error
      ? String(error)
      : "Keycloak init failed with no error details";

    diagnosticEventLog.record("auth:app", "error", "keycloak-init-failed", {
      message: errorMessage,
    });
    console.error("Keycloak initialization failed:", error);

    Sentry.captureException(new Error(errorMessage));

    useStore.setState({
      keycloakInitFailed: true,
      authenticated: false,
      accessToken: null,
    });
  });

export default keycloakInstance;
